<template>
  <div class="Cadex">

    <!-- Masthead -->
    <ProjectMasthead
      title="Cadex Cloud"
      subtitle="IoT B2B platform."
      srcImage="/assets/digital/cadex/mast-preview.png"
      largeImage="/assets/digital/cadex/mast-preview@2x.png"
      bgColour="#27AAE1" />

    <!-- Testimonial -->
    <Testimonial
      shortQuote="Working with Mike was a pleasure throughout the entire process."
      attributed="Greg Funk, Director Product Line Management, Cadex Electronics"
    >
      <template slot="fullQuote">
        <p>"Mike Cassie was hired to help Cadex Electronics create a new B2B cloud platform to support its suite of industry leading battery analyzer and charger products. Mike helped lead our product and engineering team, marketing team and executives through a discovery process to envision and document the user and business requirements of the platform, taking into account the wide ranging needs of our customers. Building on that work, along with further research and discussions, he presented a clear road map for the project; the design and user experience of the core product, the technical solution and marketing solution. He then managed the development team on the actual build. Working with Mike was a pleasure throughout the entire process. Mike was very responsive to any requests and clear and concise with all communications. I hope to work closely with Mike on future projects."</p>
      </template>
    </Testimonial>

    <!-- Project Details -->
    <div class="details">

      <!-- Intro -->
      <ContentSection title="Introduction">
        <p>I was tasked with helping Cadex Electronics transition and integrate their successful line of battery testing hardware onto a cloud-enabled solution.</p>

        <p>The platform would allow users to control and monitor up to hundreds of hardware devices online, where traditionally that activity would be tied to the individual devices or to a local PC connected to a small subset of devices.</p>

        <p>The project included many challenges; namely building a platform around hardware that wasn’t yet finalized, bringing a new kind of service to market that didn’t currently exist and designing a platform that was very forward looking, able to anticipate future hardware solutions the company might roll out.</p>
      </ContentSection>

      <!-- Role -->
      <ContentSection title="My Role">
        <h3>Project Management</h3>
        <p>I was responsible for managing all facets of the project: planning, budgets, timelines and deliverables, and the development team.</p>

        <h3>Product &amp; Market Strategy</h3>
        <p>I worked closely with company leadership to map out a vision of the product; it's features, how to launch it, and how to grow it in the marketplace. This included strategy on how to transition existing clients to the platform, attract new clients, and how to price and market the platform.</p>

        <h3>Product Design</h3>
        <p>While the company’s business goals were clear, they did not have the internal resources or experience to translate those to a cloud-based solution. I had to work with their internal software and hardware development teams and their executive stakeholders to formulate the vision and execution strategy for this new product.</p>

        <h3>UX/UI Design &amp; Research</h3>
        <p>I was responsible for crafting the project’s UX and UI. This included generating and documenting functional requirements, research, generating personas and journey flows, IA, prototypes and visual design of varying fidelities.</p>

        <h3>Front End Development</h3>
        <p>I was the sole front end developer on the project, coding out all the UI in VueJS. I developed the UI coding re-usable single file components.</p>
      </ContentSection>

      <!-- Research -->
      <ContentSection title="Research &amp; Insights">
        <p>This was a new space in this particular vertical and there was little industry research to tap into. I focused initial research on the current hardware-based UI and the PC-based UI most users were conditioned to interreacting with on a daily basis. This was done by using the actual interfaces so they became familiar to me, as well as ethnographic studies of current users of the existing hardware and software-based UIs.</p>

        <ProjectImage
          srcImage="/assets/digital/cadex/batshop-900.png"
          largeImage="/assets/digital/cadex/batshop-1800.png"
          :canZoom="true"
          caption="Analysis of existing desktop solution" />

        <p>I also drew up personas as our user groups were spread very wide in their needs, expertise and goals. The nature of the platform involved at one end, very complex analytical data used in product research and development by engineers; while at the other end of the spectrum were simple one metric pass/fail ratings used by non-professional storefront users. This left a question about what opportunities and issues were in the middle of all this.</p>

        <ProjectImage
          srcImage="/assets/digital/cadex/personas-400.png"
          largeImage="/assets/digital/cadex/personas-1800.png"
          :srcSize="400"
          :canZoom="true"
          caption="Persona study" />

        <p>The business did not have a clear vision of the revenue model around this service. Knowing that would have a significant impact on the final product outcome, I also took the time to map out options for the business, focusing on how and where different models would impact functional requirements and the user acquisition strategies.</p>

        <p>One significant challenge on the project was the inability to access end users for research to better understand their wants and needs. Due to strategic business reasons, the business did not want to bring this project to their user’s attention until the hardware was further along in its development cycle. While high level discussions with a limited pool of existing customers had taken place, the business did not want to run the risk of over-promising and under delivering something that could threaten key existing relationships.</p> 

        <p>As such, we had to rely on internal users within the business and limited feedback from a select handful of external users. That internal feedback did confirm many initial assumptions about the core product design but the risk of limited input at this point was undeniable. The business was aware of this, understood the scope and budget risks posed by this and agreed that formal, in-depth testing would be done with one or two existing key clients when the hardware was ready.</p>

        <p>In spite of the challenges around limited research opportunities, several points revealed themselves during this initial research:</p>

        <ol>
          <li>
            <h3>The Cloud Effect</h3>
            <p><strong>Challenge:</strong> The business’ current PC-based software solution was an industry standard and in spite of some UX shortcomings in it, it was a heavily embedded solution and would present some challenges to move users away from it.</p>

            <p><strong>Opportunity:</strong> The opportunity here was clear; the ability to use, control and analyze data across all the devices in an organization in one place was monumental – on two fronts. One; end users could now leverage that online data more easily and quickly and not be tied to limited insights from a subset of devices, or have to spend time exporting data from multiples sources and piecing it back together. And two; the platform would compile this data to be used internally to build better devices based on real-world usage patterns.</p>
          </li>
          <li>
            <h3>Where’s The Middle Ground?</h3>
            <p><strong>Challenge:</strong> It was clear that the current solution captured two distinct end points. Namely, a complicated, analytically deep desktop UI at one end and the simplicity of green/red pass/fail indicators on some of the hardware interfaces. This meant a very diverse UX experience needed to be address in the platform.</p>

            <p><strong>Opportunity:</strong> It was apparent to me that the current solution left a gaping middle ground in functionality and user needs. This was an opportunity to grow adoption with users who had avoided more complex analytical solutions because the jump to the PC software was too extreme and abrupt. While the new platform needed to address both extremes of users, it would also provide user experiences in between those extremes, which would open up the platform to those untapped users in the market. This translated to what I termed a “scalable UI” that could be easily configured to match a particular user’s expertise and needs.</p>
          </li>
          <li>
            <h3>Big Data</h3>
            <p><strong>Challenge:</strong> The nature of the battery testing hardware is that it produces massive amounts of data – and that data can change significantly depending on the particular kind of testing hardware being employed from the business’ fleet of hardware solutions. This meant that we were facing a significant Big Data challenge.</p>

            <p><strong>Opportunity:</strong> Somewhat like the ‘scalable UI’ for users, the data challenge shifted based on the kind of user and device. I saw this as an opportunity to reinforce in the platform’s design, a highly adaptable UI that could shift based not only on the user type, but also the <em>kind of data and activates</em> the user was concerned about. I designed the UI so that it could handle any amount and type of data and could easily be configured based on any user needs.</p>

            <ProjectImage
            srcImage="/assets/digital/cadex/ui-sketch-860.jpg"
            largeImage="/assets/digital/cadex/ui-sketch-1800.jpg"
            :canZoom="true"
            caption="Sketches for configurable and scalable UI" />

          </li>
        </ol>
      </ContentSection>

      <!-- Design -->
      <ContentSection title="Design &amp; Development">
        <p>I drew up a requirement documentation and associated flow diagrams for the project to confirm understanding of the business and functional goals and presented that to key stakeholders.</p>

        <ProjectImage
          srcImage="/assets/digital/cadex/overall-flow-900.png"
          largeImage="/assets/digital/cadex/overall-flow-1800.png"
          :canZoom="true"
          caption="Functional flow diagram" />

        <p>The business wanted a highly agile process so internal stakeholders could test and review components as they were ready and make certain the platform design could be supported by the hardware design. I drew up sprints for the core features and each completed sprint would be released for internal user review, addressing and backlogging issues as each release was tested.</p>

        <p>Once the first set of hardware was ready, much of the core platform would be in place and more formal, external user testing would take place.</p>

        <p>We then moved into development. For each sprint, I did my visual and UX design in-browser within our actual development codebase. Functionality was then completed by the dev team, using a growing API for the platform, which would eventually also be available as a client-facing API. Units tests were written and UAT done and then the sprint was released to the business for internal review – and issues were logged and addressed or back logged as made most sense.</p>

        <ProjectImageLaptop
          srcImage="/assets/digital/cadex/marketing-site-690.jpg"
          largeImage="/assets/digital/cadex/marketing-site-1800.jpg"
          :canZoom="true"
          caption="Marketing website" />

        <ProjectImageLaptop
          srcImage="/assets/digital/cadex/dashboard-690.png"
          largeImage="/assets/digital/cadex/dashboard-1800.png"
          :canZoom="true"
          caption="Administration dashboard" />

        <!-- <ProjectImageLaptop
          srcImage="/assets/digital/cadex/batteries-charts-690.png"
          largeImage="/assets/digital/cadex/batteries-charts-1800.png"
          :canZoom="true"
          caption="Batteries View" /> -->

        <ProjectImageLaptop
          srcImage="/assets/digital/cadex/services-list-690.png"
          largeImage="/assets/digital/cadex/services-list-1800.png"
          :canZoom="true"
          caption="Services view" />

        <ProjectImageLaptop
          srcImage="/assets/digital/cadex/batteries-recording-full"
          :isVideo="true"
          caption="User configuring display and viewing data" />

        <p>The configurable and scalable UI could be configured for users by administrators, deciding what to show and what a user could interact with.</p>

        <ProjectImageMobile
          srcImage1="/assets/digital/cadex/display-config-unlocked-330.png"
          largeImage1="/assets/digital/cadex/display-config-unlocked-660.png"
          caption1="At first, a user has unrestricted access to all data"
          srcImage2="/assets/digital/cadex/display-config-660"
          caption2="Then an administrator adds a new display configuration"
          :isVideo2="true"
          :showTab="false" />

        <ProjectImageMobile
          srcImage1="/assets/digital/cadex/user-groups-660"
          caption1="Then the administrator assigns the display configuration and sets permissions for a user and group"
          :isVideo1="true"
          srcImage2="/assets/digital/cadex/display-config-locked-330.png"
          largeImage2="/assets/digital/cadex/display-config-locked-660.png"
          caption2="User now has restricted access to data"
          :showTab="false" />

      </ContentSection>

      <!-- Outcome -->
      <ContentSection title="Outcome &amp; Learnings">
        <p>As the platform progressed to near-complete, it became apparent that the initial hardware would not be ready in the originally anticipated timeframe. This posed a serious problem in that the platform was now at the point where we needed hardware to begin external user testing. In order to not lose traction, we created a hardware simulator that could be used to generate real-word data to populate this platform. The platform is now beginning user testing with some key clients of the business and initial reaction has been exceptionally positive, though there is still considerable testing to do with clients in other verticals.</p>

        <p>We are also investigating building a migration/connector tool for existing PC-based users to allow them to easily transition their existing PC-based workflow and data to the cloud platform.</p>
      </ContentSection>      

    </div>

    <Pagination
      prevLabel="Erbium"
      nextLabel="Want2Work"
      prevRoute="Erbium"
      nextRoute="W2W" />


    <Footer />
  </div>
</template>

<script>
import ProjectMasthead from '@/components/ProjectMasthead.vue'
import Footer from '@/components/Footer.vue'
import Pagination from '@/components/Pagination.vue'
import Testimonial from '@/components/Testimonial.vue'
import ContentSection from '@/components/ContentSection.vue'
import ProjectImage from '@/components/ProjectImage.vue'
import ProjectImageMobile from '@/components/ProjectImageMobile.vue'
import ProjectImageLaptop from '@/components/ProjectImageLaptop.vue'

export default {
  name: 'Cadex',
  components: {
    ProjectMasthead,
    Footer,
    Pagination,
    Testimonial,
    ContentSection,
    ProjectImage,
    ProjectImageMobile,
    ProjectImageLaptop
  },
}
</script>

<style lang="scss" scoped>
  .Cadex {
    background: #fff;
  }
</style>
